/**
 * Client-side image processing utilities
 * Handles image resizing, optimization, and validation
 */

export interface ProcessedImage {
  file: File;
  dataUrl: string;
  width: number;
  height: number;
  originalSize: number;
  processedSize: number;
}

export interface ImageProcessingOptions {
  maxWidth: number;
  maxHeight: number;
  quality: number; // 0-1
  maintainAspectRatio: boolean;
  outputFormat: 'image/jpeg' | 'image/png' | 'image/webp';
}

const DEFAULT_OPTIONS: ImageProcessingOptions = {
  maxWidth: 1200,
  maxHeight: 1200,
  quality: 0.85,
  maintainAspectRatio: true,
  outputFormat: 'image/jpeg',
};

/**
 * Validates if a file is an acceptable image
 */
export function isValidImageFile(file: File): boolean {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  return validTypes.includes(file.type);
}

/**
 * Gets the maximum file size allowed (in bytes)
 */
export function getMaxFileSize(): number {
  return 10 * 1024 * 1024; // 10MB
}

/**
 * Validates file size
 */
export function isValidFileSize(file: File): boolean {
  return file.size <= getMaxFileSize();
}

/**
 * Loads an image file and returns an HTMLImageElement
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));

      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Calculates new dimensions while maintaining aspect ratio
 */
function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number,
  maintainAspectRatio: boolean
): { width: number; height: number } {
  if (!maintainAspectRatio) {
    return { width: maxWidth, height: maxHeight };
  }

  const aspectRatio = originalWidth / originalHeight;

  let width = originalWidth;
  let height = originalHeight;

  // Scale down if larger than max dimensions
  if (width > maxWidth) {
    width = maxWidth;
    height = width / aspectRatio;
  }

  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }

  return {
    width: Math.round(width),
    height: Math.round(height),
  };
}

/**
 * Processes an image file: resizes, compresses, and converts format
 */
export async function processImage(
  file: File,
  options: Partial<ImageProcessingOptions> = {}
): Promise<ProcessedImage> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Validate file type
  if (!isValidImageFile(file)) {
    throw new Error('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
  }

  // Validate file size
  if (!isValidFileSize(file)) {
    throw new Error(`File size exceeds ${getMaxFileSize() / (1024 * 1024)}MB limit.`);
  }

  // Load the image
  const img = await loadImage(file);

  // Calculate new dimensions
  const { width, height } = calculateDimensions(
    img.naturalWidth,
    img.naturalHeight,
    opts.maxWidth,
    opts.maxHeight,
    opts.maintainAspectRatio
  );

  // Create canvas and draw resized image
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Use high-quality image smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Draw image on canvas
  ctx.drawImage(img, 0, 0, width, height);

  // Convert to blob
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      opts.outputFormat,
      opts.quality
    );
  });

  // Create new file from blob
  const processedFile = new File(
    [blob],
    file.name.replace(/\.[^.]+$/, opts.outputFormat === 'image/jpeg' ? '.jpg' : '.png'),
    { type: opts.outputFormat }
  );

  // Get data URL for preview
  const dataUrl = canvas.toDataURL(opts.outputFormat, opts.quality);

  return {
    file: processedFile,
    dataUrl,
    width,
    height,
    originalSize: file.size,
    processedSize: processedFile.size,
  };
}

/**
 * Formats file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Creates a preview data URL from a file
 */
export async function createPreviewUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      resolve(e.target?.result as string);
    };

    reader.onerror = () => {
      reject(new Error('Failed to create preview'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Batch processes multiple images
 */
export async function processImages(
  files: File[],
  options: Partial<ImageProcessingOptions> = {},
  onProgress?: (processed: number, total: number) => void
): Promise<ProcessedImage[]> {
  const results: ProcessedImage[] = [];

  for (let i = 0; i < files.length; i++) {
    const processed = await processImage(files[i], options);
    results.push(processed);

    if (onProgress) {
      onProgress(i + 1, files.length);
    }
  }

  return results;
}
